import React, { useState, useEffect } from 'react';

interface Family {
  id: string;
  familyName: string;
  primaryContactFirstName: string;
  primaryContactLastName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  textOptInPrimary?: boolean;
  primaryContactDutyRole?: string;
  secondaryContactFirstName?: string;
  secondaryContactLastName?: string;
  secondaryContactEmail?: string;
  secondaryContactPhone?: string;
  textOptInSecondary?: boolean;
  secondaryContactDutyRole?: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  monthlyFee: number;
  membershipStatus: string;
  userPassword?: string;
  mustChangePassword?: boolean;
}

interface EditFamilyFormProps {
  family: Family;
  onClose: () => void;
  onSuccess: () => void;
}

export default function EditFamilyForm({ family, onClose, onSuccess }: EditFamilyFormProps) {
  const [formData, setFormData] = useState<Family>(family);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [mustChangePassword, setMustChangePassword] = useState(true);
  const [userInfo, setUserInfo] = useState<any>(null);

  useEffect(() => {
    fetchUserInfo();
  }, []);

  const fetchUserInfo = async () => {
    try {
      const token = localStorage.getItem('authToken');
      // Get family data which includes user info
      const response = await fetch(`/api/families/${family.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.success) {
        if (data.data.user) {
          // User account exists
          setUserInfo({
            email: data.data.user.email,
            currentPassword: data.data.user.currentPassword,
            mustChangePassword: data.data.user.mustChangePassword || false,
            accountExists: true
          });
        } else {
          // No user account exists - will be created on password update
          setUserInfo({
            email: family.primaryContactEmail,
            currentPassword: null,
            mustChangePassword: true,
            accountExists: false
          });
        }
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
  };

  const handleInputChange = (field: keyof Family, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordUpdate = async () => {
    if (!newPassword || newPassword.length < 6) {
      setErrors(['Password must be at least 6 characters long']);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/families/${family.id}/user-password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          newPassword,
          mustChangePassword
        })
      });

      const data = await response.json();

      if (data.success) {
        setShowPasswordSection(false);
        setNewPassword('');
        
        // Update local user info - mark account as now existing
        setUserInfo(prev => ({
          ...prev,
          currentPassword: mustChangePassword ? newPassword : null,
          mustChangePassword: mustChangePassword,
          accountExists: true
        }));
        
        // Show success message
        const wasCreated = userInfo && !userInfo.accountExists;
        const message = wasCreated 
          ? `✅ User account created successfully!\n\nLogin Email: ${userInfo.email}\nPassword: ${newPassword}\n\n${mustChangePassword ? '⚠️ User must change password on first login' : ''}`
          : `✅ Password updated successfully!${mustChangePassword ? '\n\nNew password: ' + newPassword : ''}`;
        
        alert(message);
        
        // Clear any previous errors
        setErrors([]);
      } else {
        setErrors([data.message]);
      }
    } catch (error) {
      console.error('Error updating password:', error);
      setErrors(['Failed to update password. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors([]);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/families/${family.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        onSuccess();
      } else {
        setErrors(data.errors?.map((err: any) => err.msg) || [data.message]);
      }
    } catch (error) {
      console.error('Error updating family:', error);
      setErrors(['Failed to update family. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to permanently DELETE the ${family.familyName}?\n\n⚠️ WARNING: This action CANNOT be undone!\n\n• The family will be permanently removed from the database\n• All associated ranch girls will also be deleted\n• All data will be lost forever\n\nType "DELETE" below to confirm:`)) {
      return;
    }

    // Second confirmation with typed verification
    const confirmation = prompt('Type "DELETE" (in capital letters) to confirm permanent deletion:');
    if (confirmation !== 'DELETE') {
      alert('Deletion cancelled. You must type "DELETE" exactly to confirm.');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/families/${family.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        onSuccess();
      } else {
        setErrors([data.message]);
      }
    } catch (error) {
      console.error('Error deleting family:', error);
      setErrors(['Failed to delete family. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
      <div className="bg-white rounded-t-xl sm:rounded-xl max-w-4xl w-full max-h-[92vh] sm:max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="p-4 sm:p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Edit {family.familyName}</h3>
                <p className="text-sm text-gray-600">Manage family information and user account</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {errors.length > 0 && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="text-red-800 font-medium mb-2">Please fix the following errors:</h4>
                <ul className="list-disc list-inside text-red-700 text-sm">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Family Information */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Family Information</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Family Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.familyName}
                      onChange={(e) => handleInputChange('familyName', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Monthly Fee <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        required
                        value={formData.monthlyFee}
                        onChange={(e) => handleInputChange('monthlyFee', parseFloat(e.target.value))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        value={formData.membershipStatus}
                        onChange={(e) => handleInputChange('membershipStatus', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      >
                        <option value="active">Active</option>
                        <option value="pending">Pending</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* User Account Management */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-medium text-blue-900">User Account Management</h4>
                  <button
                    type="button"
                    onClick={() => setShowPasswordSection(!showPasswordSection)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    {showPasswordSection ? 'Hide' : userInfo && !userInfo.accountExists ? 'Create Account' : 'Manage Password'}
                  </button>
                </div>

                {userInfo && !userInfo.accountExists && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <p className="text-yellow-800 text-sm">
                      ℹ️ <strong>No user account exists yet.</strong> Click "Create Account" to set up login credentials for this family.
                    </p>
                  </div>
                )}

                {userInfo && (
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-blue-700">Login Email:</span>
                      <span className="font-mono bg-blue-100 px-2 py-1 rounded">{userInfo.email}</span>
                    </div>
                    {userInfo.currentPassword && (
                      <div className="flex justify-between">
                        <span className="text-blue-700">Current Password:</span>
                        <span className="font-mono bg-yellow-100 px-2 py-1 rounded font-bold">
                          {userInfo.currentPassword}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-blue-700">Must Change Password:</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        userInfo.mustChangePassword 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {userInfo.mustChangePassword ? 'Yes - Required' : 'No'}
                      </span>
                    </div>
                  </div>
                )}

                {showPasswordSection && (
                  <div className="mt-4 p-4 bg-white rounded border border-blue-300">
                    <h5 className="font-medium text-gray-900 mb-3">
                      {userInfo && !userInfo.accountExists ? 'Create User Account' : 'Update Password'}
                    </h5>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {userInfo && !userInfo.accountExists ? 'Set Password' : 'New Password'} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter password (min 6 characters)"
                        />
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="mustChangePassword"
                          checked={mustChangePassword}
                          onChange={(e) => setMustChangePassword(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="mustChangePassword" className="ml-2 block text-sm text-gray-900">
                          Require user to change password on next login
                        </label>
                      </div>
                      <div className="flex space-x-3">
                        <button
                          type="button"
                          onClick={handlePasswordUpdate}
                          disabled={loading || !newPassword}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? (userInfo && !userInfo.accountExists ? 'Creating...' : 'Updating...') : (userInfo && !userInfo.accountExists ? 'Create Account' : 'Update Password')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowPasswordSection(false);
                            setNewPassword('');
                            setErrors([]);
                          }}
                          className="px-4 py-2 text-gray-600 hover:text-gray-800"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Primary Contact */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Primary Contact</h4>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.primaryContactFirstName}
                        onChange={(e) => handleInputChange('primaryContactFirstName', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.primaryContactLastName}
                        onChange={(e) => handleInputChange('primaryContactLastName', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.primaryContactEmail}
                      onChange={(e) => handleInputChange('primaryContactEmail', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.primaryContactPhone}
                      onChange={(e) => handleInputChange('primaryContactPhone', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Duty Parent Role</label>
                    <select
                      value={formData.primaryContactDutyRole || 'none'}
                      onChange={(e) => handleInputChange('primaryContactDutyRole', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    >
                      <option value="none">None</option>
                      <option value="troop_mom">Troop Mom</option>
                      <option value="troop_dad">Troop Dad</option>
                      <option value="surrogate_mom">Surrogate Mom</option>
                      <option value="surrogate_dad">Surrogate Dad</option>
                    </select>
                  </div>

                  <div className="col-span-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.textOptInPrimary || false}
                        onChange={(e) => handleInputChange('textOptInPrimary', e.target.checked)}
                        className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">📱 Opt in for text notifications</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Secondary Contact */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Secondary Contact (Optional)</h4>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                      <input
                        type="text"
                        value={formData.secondaryContactFirstName || ''}
                        onChange={(e) => handleInputChange('secondaryContactFirstName', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                      <input
                        type="text"
                        value={formData.secondaryContactLastName || ''}
                        onChange={(e) => handleInputChange('secondaryContactLastName', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={formData.secondaryContactEmail || ''}
                      onChange={(e) => handleInputChange('secondaryContactEmail', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <input
                      type="tel"
                      value={formData.secondaryContactPhone || ''}
                      onChange={(e) => handleInputChange('secondaryContactPhone', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Duty Parent Role</label>
                    <select
                      value={formData.secondaryContactDutyRole || 'none'}
                      onChange={(e) => handleInputChange('secondaryContactDutyRole', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    >
                      <option value="none">None</option>
                      <option value="troop_mom">Troop Mom</option>
                      <option value="troop_dad">Troop Dad</option>
                      <option value="surrogate_mom">Surrogate Mom</option>
                      <option value="surrogate_dad">Surrogate Dad</option>
                    </select>
                  </div>

                  <div className="col-span-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.textOptInSecondary || false}
                        onChange={(e) => handleInputChange('textOptInSecondary', e.target.checked)}
                        className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">📱 Opt in for text notifications</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Address</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Street Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        City <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        State <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ZIP Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.zipCode}
                      onChange={(e) => handleInputChange('zipCode', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleDelete}
                disabled={loading}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Deleting...' : '🗑️ Delete Family'}
              </button>
              
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="px-6 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-green/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Updating...' : 'Update Family'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
} 